import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { TypographySmall } from '@/components/ui/typography'
import { usePaymentContext } from '@/context-state/PaymentContext.jsx'
import { Icon } from '@iconify/react'
import { Children, cloneElement, isValidElement } from 'react'
import { togglePricingPlansDialog } from '@/hooks/usePricingPlans.jsx'
import { toggleAppBanner } from '@/hooks/useAppBanner.jsx'
import { useEntitlementsAvailability } from '@/hooks/useEntitlementsAvailability.jsx'

export const EntitlementsKeys = {
    BRANDS_FOLLOW: 'brands_follow',
    SEATS_ALLOWED: 'seats_allowed',
    AD_SPEND_CAP: 'ad_spend_cap',
    ACTIVE_AD_ACCOUNTS: 'active_ad_accounts',
    SPACES_ALLOWED: 'spaces_allowed',
    API_CREDITS: 'api_credits',
    LIVE_ADS_ANALYZED: 'live_ads_analyzed',
    // Free-tier feature fences (client-only, see consts/freeTierEntitlements.js)
    VIEWS_MANAGE: 'views_manage',
    REPORTS_MANAGE: 'reports_manage',
    WEEKLY_INSIGHTS: 'weekly_insights',
    ADVERTISER_ADS: 'advertiser_ads',
    ADVERTISER_VIEW_ALL: 'advertiser_view_all',
    CREATIVE_INSIGHTS: 'creative_insights',
    CREATIVE_IDEAS: 'creative_ideas',
    CREATIVE_COMMENTS: 'creative_comments',
    TAG_CONFIG: 'tag_config',
}

export const EntitlementsGuard = ({ children, entitlementKey, message = "Upgrade to unlock", bannerType, source, hideContent = false }) => {

    const { available, type } = useEntitlementsAvailability(entitlementKey)

    if (available) {
        return children
    }

    // Some surfaces should simply disappear when not entitled (e.g. a re-sync
    // action Free can't use) rather than show a locked/crowned control.
    if (hideContent) {
        return null
    }

    // Clone and modify children to add event handlers - that way we prevent the user from interacting with the element even it it's alter the DOM manually
    const modifiedChildren = Children.map(children, (child) => {
        // Only process valid React elements
        if (!isValidElement(child)) {
            return child
        }

        // Prevent click events. When a bannerType is provided (Free-tier fences),
        // surface the shared AppBanner instead of the pricing dialog; otherwise
        // keep the existing dialog behavior.
        const enforce = (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (bannerType) {
                toggleAppBanner(bannerType, source)
            } else {
                togglePricingPlansDialog(true)
            }
            return false
        }
        const enforcePricingPlansDialog = enforce

        // Clone the element with additional props
        return cloneElement(child, {
            onClick: enforcePricingPlansDialog,
            onMouseDown: enforcePricingPlansDialog,
            onMouseUp: enforcePricingPlansDialog,
            onTouchStart: enforcePricingPlansDialog,
            onTouchEnd: enforcePricingPlansDialog,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    enforcePricingPlansDialog(e)
                }
                // Call original onKeyDown if it exists
                if (child.props.onKeyDown) {
                    child.props.onKeyDown(e)
                }
            },
            // Disable the element
            // disabled: true,
            // Add aria attributes for accessibility
            // 'aria-disabled': true,
            className: `${child.props.className || ''} opacity-50`
        })
    })

    // If user doesn't have access and hideContent is false, render with tooltip
    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="inline-block relative">
                        <Icon icon="solar:crown-bold" className="w-5 h-5 absolute -top-2 -right-1 text-primary-600 rotate-[25deg] z-10" />
                        {modifiedChildren}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <TypographySmall>
                        <span className="flex items-center gap-1">
                            <Icon icon="solar:crown-bold" className="w-4 h-4 text-primary-600" />
                            {message}
                        </span>
                    </TypographySmall>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )


}